import { useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { DownloadOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS, parseISO } from "utils/datetime";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog from "components/Dialog";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";

import { EChecklistSubmissionDialogStep } from "pages/Deal/DueDiligence/constants";

import { ChecklistSnapshotFileStatus } from "entities/Package/constants";
import {
  snapshotPDFFileSignedURLRetrieve,
  snapshotZIPFileSignedURLRetrieve,
  useCompanyPackageSnapshot,
} from "entities/Package/sdk";
import {
  useCompanyPackageSnapshotProcessing,
  useZipProcessing,
} from "entities/TableStorage/hooks";

import DownloadButton from "./DownloadButton";

export interface ISubmitChecklistDialog {
  open: EChecklistSubmissionDialogStep;
  onClose: () => void;
}

const SubmittedChecklistDownloadDialog = ({
  open,
  onClose,
}: ISubmitChecklistDialog) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);
  const companyPackageId = useMemo(
    () => Number(params.packageId),
    [params.packageId]
  );

  const [searchParams] = useSearchParams();

  const snapshotId = useMemo(
    () =>
      !_.isNil(searchParams.get("snapshotId"))
        ? Number(searchParams.get("snapshotId"))
        : undefined,
    [searchParams]
  );

  const {
    data: snapshot,
    isLoading,
    mutate: mutateSnapshot,
  } = useCompanyPackageSnapshot({
    dealId,
    companyPackageId,
    snapshotId: snapshotId as number,
    enabled: _.isNumber(snapshotId),
  });

  const onUpdate = useCallback(() => mutateSnapshot(), [mutateSnapshot]);

  useCompanyPackageSnapshotProcessing({
    successEventHandler: onUpdate,
  });

  useZipProcessing({
    successEventHandler: onUpdate,
  });

  const downloadSnapshotPDF = useCallback(() => {
    if (!_.isNil(snapshot?.id)) {
      const toastId = toast.loading("Retrieving file...");

      snapshotPDFFileSignedURLRetrieve({
        dealId,
        companyPackageId,
        snapshotId: snapshot.id,
      })
        .then((url) =>
          downloadFileWithFileName({
            toastId,
            fileUrl: url,
            fileName: snapshot.pdf_file.original_file_name,
          })
        )
        .catch(handleInvalidRequest);
    }
  }, [snapshot, dealId, companyPackageId]);

  const downloadSnapshotZIP = useCallback(() => {
    if (!_.isNil(snapshot?.id)) {
      const toastId = toast.loading("Retrieving file...");

      snapshotZIPFileSignedURLRetrieve({
        dealId,
        companyPackageId,
        zipId: snapshot.dd_zip_file.id,
      })
        .then((url) =>
          downloadFileWithFileName({
            toastId,
            fileUrl: url,
            fileName: snapshot.dd_zip_file.file.original_file_name,
          })
        )
        .catch(handleInvalidRequest);
    }
  }, [dealId, companyPackageId, snapshot]);

  const downloadSnapshotPDFandZIP = useCallback(() => {
    downloadSnapshotPDF();
    downloadSnapshotZIP();
  }, [downloadSnapshotPDF, downloadSnapshotZIP]);

  const displayPDFPendingStatus = useMemo(
    () => snapshot?.pdf_file_status === ChecklistSnapshotFileStatus.IN_PROGRESS,
    [snapshot]
  );

  const displayPDFFailedStatus = useMemo(
    () => snapshot?.pdf_file_status === ChecklistSnapshotFileStatus.FAILED,
    [snapshot]
  );

  const displayZipPendingStatus = useMemo(
    () => snapshot?.zip_file_status === ChecklistSnapshotFileStatus.IN_PROGRESS,
    [snapshot]
  );

  const displayZipFailedStatus = useMemo(
    () => snapshot?.zip_file_status === ChecklistSnapshotFileStatus.FAILED,
    [snapshot]
  );

  const downloadPDFTooltip = useMemo(() => {
    if (displayPDFPendingStatus) {
      return "Please wait while we generate the pdf file.";
    }
    if (displayPDFFailedStatus) {
      return "The pdf generation has been unsuccessful.";
    }
    return undefined;
  }, [displayPDFFailedStatus, displayPDFPendingStatus]);

  const downloadZIPTooltip = useMemo(() => {
    if (displayZipPendingStatus) {
      return "Please wait while we generate the zip file.";
    }
    if (displayZipFailedStatus) {
      return "The zip generation has been unsuccessful.";
    }
    return undefined;
  }, [displayZipFailedStatus, displayZipPendingStatus]);

  const downloadZIPAndPDFTooltip = useMemo(() => {
    if (
      (displayPDFPendingStatus || displayZipPendingStatus) &&
      !(displayPDFFailedStatus || displayZipFailedStatus)
    ) {
      return "Please wait while we generate the zip and pdf files.";
    }
    if (displayZipFailedStatus || displayPDFFailedStatus) {
      return "The zip or pdf file generation has been unsuccessful.";
    }
    return undefined;
  }, [
    displayZipFailedStatus,
    displayZipPendingStatus,
    displayPDFFailedStatus,
    displayPDFPendingStatus,
  ]);

  return (
    <Dialog
      open={open === EChecklistSubmissionDialogStep.DOWNLOAD}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <Stack padding={2} gap={2.5}>
        <Stack
          padding={theme.spacing(2, 2, 1.5, 2)}
          sx={{ background: colors.blue10, borderRadius: 2, gap: 1 }}
        >
          <Text variant="h2">Checklist successfully submitted!</Text>
          {isLoading ? (
            <Skeleton variant="text" height={22} width={300} />
          ) : (
            !_.isNil(snapshot?.id) && (
              <Text variant="text2" color={colors.gray80}>
                Submitted on{" "}
                {format(parseISO(snapshot.created_at), FORMATS.FRONTEND.DATE)}{" "}
                by {snapshot.created_by.first_name}{" "}
                {snapshot.created_by.last_name}
              </Text>
            )
          )}
        </Stack>
        <Stack gap={0.8} paddingX={1}>
          <Text variant="text2" fontWeight={400} color={colors.gray100}>
            You can download a copy of your responses in PDF format as well as a
            ZIP file with all uploaded files. You can also choose to do this at
            a later time from the Snapshots tab on the checklist.
          </Text>
          <DownloadButton
            tooltipTitle={downloadPDFTooltip}
            onClick={downloadSnapshotPDF}
            loading={_.isNil(snapshot)}
            disabled={displayPDFPendingStatus || displayPDFFailedStatus}
            cursorNotAllowed={displayPDFFailedStatus}
          >
            <DownloadOutlinedIcon sx={{ width: "20px", height: "20px" }} />
            <Text variant="text2" fontWeight={500}>
              Download contents (PDF)
            </Text>
          </DownloadButton>
          <DownloadButton
            tooltipTitle={downloadZIPTooltip}
            onClick={downloadSnapshotZIP}
            loading={_.isNil(snapshot)}
            disabled={displayZipPendingStatus || displayZipFailedStatus}
            cursorNotAllowed={displayZipFailedStatus}
          >
            <DownloadOutlinedIcon sx={{ width: "20px", height: "20px" }} />
            <Text variant="text2" fontWeight={500}>
              Download file attachments (ZIP)
            </Text>
          </DownloadButton>
          <DownloadButton
            tooltipTitle={downloadZIPAndPDFTooltip}
            onClick={downloadSnapshotPDFandZIP}
            loading={_.isNil(snapshot)}
            disabled={
              displayPDFPendingStatus ||
              displayPDFFailedStatus ||
              displayZipPendingStatus ||
              displayZipFailedStatus
            }
            cursorNotAllowed={displayPDFFailedStatus || displayZipFailedStatus}
          >
            <DownloadOutlinedIcon sx={{ width: "20px", height: "20px" }} />
            <Text variant="text2" fontWeight={500}>
              Download both PDF & ZIP
            </Text>
          </DownloadButton>

          <Button
            onClick={onClose}
            sx={{
              width: "fit-content",
            }}
          >
            Skip & download later
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
export default SubmittedChecklistDownloadDialog;
